// react
import { ReactNode, useCallback, useMemo, useRef, useState } from 'react';

// components
import { RecurrenceDialog } from './dialogs';
import { OverlapDialog } from './dialogs/OverlapDialog';
import { SmartActionDialog } from './dialogs/SmartActionDialog';
import EntityAvailabilityDialog from './dialogs/EntityAvailabilityDialog/EntityAvailabilityDialog';

// context
import { DialogContext } from './DialogContext';

// types
import type {
  BaseDialogProps,
  DialogContextProps,
  DialogPropsMap,
  DialogTypeMap
} from './DialogProvider.types';
import { DialogType } from './DialogProvider.enums';

export const DialogProvider = ({
  children
}: {
  children: ReactNode | ReactNode[];
}) => {
  const dialogEventHandlers = useRef<
    Partial<{
      [K in DialogType]: Record<string, DialogTypeMap<BaseDialogProps>[K]>;
    }>
  >({});

  const [openDialogs, setOpenDialogs] = useState<
    { name: string; type: DialogType }[]
  >([]);

  const handleOnClose = useCallback(
    <T extends DialogType>(name: string, type: T, props: DialogPropsMap[T]) => {
      dialogEventHandlers.current[type]?.[name]?.onClose?.({ ...props, type });
      delete dialogEventHandlers.current[type]?.[name];
      setOpenDialogs((dialogs) =>
        dialogs.filter((dialog) => dialog.name !== name)
      );
    },
    []
  );

  const renderDialog = useCallback(() => {
    return openDialogs.map(({ name, type }) => {
      const config = dialogEventHandlers.current[type]?.[name];

      if (!config) return <></>;

      const { props, onOpen } = config;

      const commonProps = {
        ...props,
        onClose: (p: any) => handleOnClose(name, type, p),
        onOpen: () => onOpen?.({ type })
      };

      switch (type) {
        case DialogType.OVERLAP_WARNING:
          return <OverlapDialog {...commonProps} key={name} open />;
        case DialogType.RECURRENCE_WARNING:
          return <RecurrenceDialog {...commonProps} key={name} open />;
        case DialogType.SMART_ACTION_DIALOG:
          return <SmartActionDialog {...commonProps} key={name} open />;
        case DialogType.ENTITY_AVAILABILITY_DIALOG:
          return <EntityAvailabilityDialog {...commonProps} key={name} open />;
        default:
          return <></>;
      }
    });
  }, [handleOnClose, openDialogs]);

  const setEventHandler = useCallback(
    (
      type: DialogType,
      name: string,
      key: 'onClose' | 'onOpen',
      callback?: (props?: any & { type: DialogType }) => void
    ) => {
      dialogEventHandlers.current[type] = {
        ...(dialogEventHandlers.current[type] || {}),
        [name]: {
          ...(dialogEventHandlers.current[type]?.[name] || {}),
          [key]: callback
        }
      };
    },
    []
  );

  const setProps = useCallback(
    <DialogProps,>(type: DialogType, name: string, props?: DialogProps) => {
      dialogEventHandlers.current[type] = {
        ...(dialogEventHandlers.current[type] || {}),
        [name]: {
          ...(dialogEventHandlers.current[type]?.[name] || {}),
          props: props ?? {}
        }
      };
    },
    []
  );

  const openDialog = useCallback(
    (
      ...[name, type, props, eventHandlers]: Parameters<
        NonNullable<DialogContextProps['openDialog']>
      >
    ) => {
      setProps(type, name, props);
      if (eventHandlers?.onClose)
        setEventHandler(type, name, 'onClose', eventHandlers.onClose);
      if (eventHandlers?.onOpen)
        setEventHandler(type, name, 'onOpen', eventHandlers.onOpen);
      setOpenDialogs((prev) => [...prev, { name, type }]);
    },
    [setProps, setEventHandler]
  ) as DialogContextProps['openDialog'];

  const onClose = useCallback(
    (...[name, type, callback]: Parameters<DialogContextProps['onClose']>) => {
      setEventHandler(type, name, 'onClose', callback);
    },
    [setEventHandler]
  ) as DialogContextProps['onClose'];

  const onOpen = useCallback(
    (...[name, type, callback]: Parameters<DialogContextProps['onOpen']>) => {
      setEventHandler(type, name, 'onOpen', callback);
    },
    [setEventHandler]
  ) as DialogContextProps['onOpen'];

  const contextValue = useMemo(
    () => ({
      isOpen: openDialogs.length > 0,
      type: null,
      openDialog,
      onClose,
      onOpen
    }),
    [openDialogs, openDialog, onClose, onOpen]
  );

  return (
    <DialogContext.Provider value={contextValue}>
      {renderDialog()}
      {children}
    </DialogContext.Provider>
  );
};

export default DialogProvider;
